import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getOwnerScorecard } from "../../../api/analyticsApi";
import { num, toMoney } from "../metricValue";
import { formatUnit, sourceBadge, targetLine, previousLine, brandLabel } from "./scorecardFormat";
import "../pages/AnalyticsDashboard.css";
import "./OwnerScorecard.css";
import BrandFilter from "../../common/BrandFilter";
import useBrandFilter from "../../../hooks/useBrandFilter";
import { brandParams } from "../../../hooks/brandFilterState";

/**
 * Owner Scorecard v1 — renders the backend registry (analytics_app/scorecard).
 *
 * The page knows nothing about which metrics exist: sections, names, sources and
 * targets all come from GET /api/analytics/owner-scorecard/. Only metrics whose
 * source is Abon carry a value; every other tile shows where its number will come
 * from ("Connects to FieldRoutes", "Connects to payroll/accounting", "Roadmap") and
 * a "—", never a 0. Every value goes through metricValue via scorecardFormat.
 */

const BREAKDOWN_ROWS = 6;

function Breakdown({ title, rows, render }) {
  const list = Array.isArray(rows) ? rows.slice(0, BREAKDOWN_ROWS) : [];
  return (
    <div className="osc-breakdown">
      {title ? <div className="osc-breakdown-title">{title}</div> : null}
      {list.length ? (
        <ul>
          {list.map((r, i) => (
            <li key={`${title}-${i}`}>{render(r)}</li>
          ))}
        </ul>
      ) : (
        <div className="osc-muted">Nothing in this period.</div>
      )}
    </div>
  );
}

function MetricDetail({ metric }) {
  const d = metric.detail || {};
  switch (metric.key) {
    case "leads_by_source_brand":
      return (
        <>
          <div className="osc-value">{formatUnit(d.total, "count")}<span className="osc-unit"> leads</span></div>
          <div className="osc-split">
            <Breakdown title="By source" rows={d.by_source} render={(r) => (<><span>{r.label}</span><b>{formatUnit(r.count, "count")}</b></>)} />
            <Breakdown title="By brand" rows={d.by_brand} render={(r) => (<><span>{brandLabel(r)}</span><b>{formatUnit(r.count, "count")}</b></>)} />
          </div>
        </>
      );
    case "pipeline_value_by_stage": {
      const rows = (d.rows || []).filter((r) => String(r.stage).toUpperCase() !== "LOST");
      return (
        <>
          <div className="osc-value">{toMoney(d.open_value)}<span className="osc-unit"> open</span></div>
          <Breakdown
            rows={rows}
            render={(r) => (
              <>
                <span>{r.label} · {formatUnit(r.count, "count")}</span>
                <b>{toMoney(r.value)}</b>
              </>
            )}
          />
          <div className="osc-muted">
            {metric.basis === "deals"
              ? "Open = pending priced deals in Target, Qualify and Bid; a stage with no priced deal shows —."
              : "Valued at open quotes; a stage with no priced quote shows —."}
          </div>
        </>
      );
    }
    case "fleet": {
      const due = num(d.maintenance_due_14d);
      const overdue = num(d.maintenance_overdue);
      return (
        <>
          <div className="osc-value">{formatUnit(metric.value, "count")}<span className="osc-unit"> active vehicles</span></div>
          <div className="osc-muted">
            {formatUnit(due, "count")} maintenance due in 14 days
            {overdue ? ` · ${formatUnit(overdue, "count")} overdue` : ""}
          </div>
        </>
      );
    }
    case "bids_due_14d":
      return (
        <>
          <div className="osc-value">{formatUnit(metric.value, "count")}</div>
          {num(d.sent_without_expiry) ? (
            <div className="osc-muted">{formatUnit(d.sent_without_expiry, "count")} sent quotes have no valid-until date</div>
          ) : null}
          {num(d.undated) ? (
            <div className="osc-muted">{formatUnit(d.undated, "count")} open bids have no due date</div>
          ) : null}
        </>
      );
    case "quote_close_rate":
      return (
        <>
          <div className="osc-value">{formatUnit(metric.value, "ratio")}</div>
          <div className="osc-muted">{formatUnit(d.signed, "count")} signed of {formatUnit(d.sent, "count")} sent</div>
        </>
      );
    default:
      return <div className="osc-value">{formatUnit(metric.value, metric.unit === "breakdown" ? "count" : metric.unit)}</div>;
  }
}

function MetricTile({ metric, onOpen }) {
  const prev = previousLine(metric);
  const Tag = metric.link ? "button" : "div";
  return (
    <Tag
      className={`osc-tile${metric.connected ? "" : " is-unconnected"}${metric.unit === "breakdown" ? " is-wide" : ""}`}
      {...(metric.link ? { type: "button", onClick: () => onOpen(metric.link) } : {})}
    >
      <div className="osc-tile-top">
        <div className="osc-name">
          {metric.name}
          {metric.basis === "quotes" ? <span className="osc-basis">from quotes</span> : null}
        </div>
        <span className={`osc-badge${metric.connected ? " is-live" : ""}`}>{sourceBadge(metric)}</span>
      </div>
      <MetricDetail metric={metric} />
      {prev ? <div className="osc-muted">{prev}</div> : null}
      {metric.brand_scope === "all" ? <div className="osc-muted">All brands — not split by brand</div> : null}
      {num(metric.target) !== null || metric.link ? (
        <div className="osc-foot">
          <span>{num(metric.target) !== null ? targetLine(metric) : ""}</span>
          {metric.link ? <span className="osc-link">Open fleet →</span> : null}
        </div>
      ) : null}
      <div className="osc-desc">{metric.description}</div>
    </Tag>
  );
}

export default function OwnerScorecard() {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const { brands, show: brandShow, brand, setBrand } = useBrandFilter();

  useEffect(() => {
    let mounted = true;
    setState((s) => ({ ...s, loading: true }));
    getOwnerScorecard(brandParams(brand))
      .then((res) => mounted && setState({ loading: false, data: res?.data || null, error: null }))
      .catch((err) => mounted && setState({ loading: false, data: null, error: err }));
    return () => { mounted = false; };
  }, [brand]);

  const sections = useMemo(() => {
    const data = state.data;
    if (!data?.enabled) return [];
    return (data.sections || []).map((s) => ({
      ...s,
      metrics: (data.metrics || []).filter((m) => m.section === s.key),
    }));
  }, [state.data]);

  const data = state.data;

  return (
    <div className="analytics-dashboard osc">
      <header className="ad-head">
        <div>
          <p className="eyebrow">Analytics</p>
          <h1>Owner scorecard</h1>
          <span>
            {data?.enabled
              ? `Last ${data.window_days} days. Tiles marked Live are computed from your Abon records; the rest show which connection supplies them.`
              : "One page for how the business is doing and what needs you this week."}
          </span>
        </div>
        <BrandFilter show={brandShow} brands={brands} value={brand} onChange={setBrand} />
      </header>

      {state.loading ? <div className="ad-empty">Loading…</div> : null}
      {state.error ? (
        <div className="osc-notice">The scorecard couldn't load ({state.error?.response?.status || "network error"}). Nothing is shown rather than a guess.</div>
      ) : null}
      {!state.loading && !state.error && data && !data.enabled ? (
        <div className="osc-notice">The owner scorecard isn't turned on for this business.</div>
      ) : null}

      {sections.map((s) => (
        <section key={s.key} className="osc-section" aria-labelledby={`osc-${s.key}`}>
          <h2 id={`osc-${s.key}`}>{s.title}</h2>
          <div className="osc-grid">
            {s.metrics.map((m) => (
              <MetricTile key={m.key} metric={m} onOpen={(to) => navigate(to)} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
